import { useState } from "react";

import { Bento } from "@/components/ui/box";
import type { Box, BoxState, ListBox, Result, VisitAddress } from "@/components/ui/box.schema";
import { BentoVisit, type BentoVisitHost } from "@/components/ui/box-visit-context";

/**
 * A Home at a glance (CASE-048): Your courses as covers, then This week and
 * Needs attention as the page's pair, each row one line and its own target.
 * Specimen data only; the names are the package's fixture learners.
 */

const imam = { system: "imam" } as const;
const AT = "2026-10-23T16:00:00Z";
const rank1 = { eligible: true, blocked: null, rank: 1 };
const waiting = (word: string): BoxState => ({ class: "waiting", word });

type Course = { id: string; code: string; title: string; facts?: string; crn: string; mark?: BoxState; live?: true; group?: string; end?: string };
const COURSES: Course[] = [
  { id: "aqaid", code: "AQAID-201", title: "Aqaid 201", facts: "Session 9 · now", crn: "40112", live: true },
  { id: "fiqh", code: "FIQH-101", title: "Fiqh 101", facts: "Next session · Tue, Oct 27, 7:00 PM", crn: "40107", mark: waiting("Overdue work") },
  { id: "akhlaq-a", code: "AKHLAQ-110", title: "Akhlaq 110", facts: "Last session · Wed, Oct 21 · Held", crn: "40121" },
  { id: "akhlaq-b", code: "AKHLAQ-110", title: "Akhlaq 110", facts: "Next session · Thu, Oct 29, 6:30 PM", crn: "40122" },
  { id: "usul", code: "USUL-210", title: "Usul 210", crn: "30188", group: "Other courses", end: "Spring 2026" },
];

type Meeting = { id: string; code: string; name: string; day: string; time: string; live?: true; prepare?: true; mark?: BoxState };
const WEEK: Meeting[] = [
  { id: "aqaid-9", code: "AQAID-201", name: "Session 9 · Oct 23", day: "Today", time: "4:00 PM", live: true },
  { id: "fiqh-4", code: "FIQH-101", name: "Session 4 · Oct 23", day: "Today", time: "7:00 PM", prepare: true },
  { id: "akhlaq-2", code: "AKHLAQ-110", name: "Session 2 · Oct 24", day: "Tomorrow", time: "6:30 PM", mark: { class: "quiet", word: "Cancelled" } },
];

type Attention = { id: string; code: string; label: string; mark?: BoxState; visit?: VisitAddress; roll?: true };
const ATTENTION: Attention[] = [
  { id: "review:a1", code: "FIQH-101", label: "Case reflection · Maryam Teaching Day, attempt 1", mark: waiting("To review"), visit: "work:a1/review" },
  { id: "review:a2", code: "FIQH-101", label: "Case reflection · Zaynab al-Amīnī, attempt 2", mark: waiting("To review"), visit: "work:a2/review" },
  { id: "overdue:e7", code: "AQAID-201", label: "Weekly reflection · Baqir Salih", mark: waiting("Overdue"), visit: "enroll:p7/aqaid" },
  { id: "roll:s1", code: "AKHLAQ-110", label: "Take the roll · Wed, Oct 21, 10:00 AM", mark: waiting("Roll not recorded"), roll: true },
];

export function HomeView() {
  const [visited, setVisited] = useState<VisitAddress | null>(null);
  const [closed, setClosed] = useState<Result>({ state: "none" });
  // The specimen's host only says which record a press asked for; a Home's real host opens it (O10).
  const host: BentoVisitHost = { visit: (address) => setVisited(address), slot: null };

  const courses: ListBox<Course[], Course> = {
    kind: "list", id: "home-courses", job: "Recognize current courses and open one", label: "Your courses", owner: imam, fold: "never", detail: () => "",
    read: { status: "ready", value: COURSES, readAt: AT }, layout: "covers", home: true,
    state: (rows) => ({ class: "quiet", word: String(rows.length) }), rows: (rows) => rows,
    row: {
      key: (course) => course.id, title: (course) => course.title, course: (course) => course.code, crn: (course) => course.crn,
      nav: (course) => `/courses/${course.id}`, facts: (course) => course.facts ?? "", end: (course) => course.end,
      state: (course) => course.mark ?? null, group: (course) => course.group,
      act: (course) => (course.live ? { does: "leave", verb: "Join in Zoom", providerUrl: "https://zoom.us/j/1" } : undefined),
    },
  };
  const week: ListBox<Meeting[], Meeting> = {
    kind: "list", id: "home-week", job: "Read this week's sessions and open their Prepare in place", label: "This week", owner: imam, fold: "never", detail: () => "",
    read: { status: "ready", value: WEEK, readAt: AT }, layout: "lines", home: true,
    exits: [{ label: "My sessions", to: "/design-system?boxes=states&state=home" }],
    state: (rows) => ({ class: "quiet", word: String(rows.length) }), rows: (rows) => rows,
    row: {
      key: (meeting) => meeting.id, title: (meeting) => meeting.name, course: (meeting) => meeting.code, nav: (meeting) => `/sessions/${meeting.id}`,
      end: (meeting) => meeting.time, group: (meeting) => meeting.day, state: (meeting) => meeting.mark ?? null,
      act: (meeting) => (meeting.live ? { does: "leave", verb: "Join in Zoom", providerUrl: "https://zoom.us/j/1" } : meeting.prepare ? { does: "open", verb: "Prepare" } : undefined),
      task: (meeting) => (meeting.prepare ? {
        render: () => <p className="m-0 text-ui text-foreground-soft">Before the session: read the two rulings.</p>,
        commit: { verb: "Publish 1 item", cap: "When you publish", effects: () => [{ id: "before", sentence: "Students read the new text" }], blockers: () => [], run: async () => ({ state: "none" }) },
        result: { state: "none" },
      } : undefined),
    },
  };
  const attention: ListBox<Attention[], Attention> = {
    kind: "list", id: "home-attention", job: "Visit the record needing judgement, or close its session in the row", label: "Needs attention", owner: imam, fold: "never", detail: () => "",
    read: { status: "ready", value: ATTENTION, readAt: AT }, layout: "lines", home: true,
    candidate: rank1, offers: "review:a2",
    state: (rows) => ({ class: "quiet", word: String(rows.length) }), rows: (rows) => rows,
    row: {
      key: (item) => item.id, title: (item) => item.label, course: (item) => item.code, visit: (item) => item.visit, state: (item) => item.mark ?? null,
      // Only the roll's Close and the offered row draw a word; every other row is its destination (CASE-048).
      act: (item) => (item.roll ? { does: "open", verb: "Close" } : item.id === "review:a2" && item.visit ? { does: "visit", verb: "Review", visit: item.visit } : undefined),
      task: (item) => (item.roll ? {
        render: () => <p className="m-0 text-ui text-foreground-soft">Mark who came, then close Session 1.</p>,
        commit: {
          verb: "Close Session 1", cap: "When you close", effects: () => [{ id: "roll", sentence: "Record the roll for 3 learners" }], blockers: () => [],
          run: async () => {
            const next: Result = { state: "confirmed", at: AT, effects: ["Session 1 is closed"], remains: [] };
            setClosed(next);
            return next;
          },
        },
        result: closed,
      } : undefined),
    },
  };

  return (
    <BentoVisit.Provider value={host}>
      <Bento id="home-specimen" journey="home-specimen" boxes={[courses, week, attention] as Box[]} pair={["home-week", "home-attention"]} />
      <p role="status" data-visited={visited ?? undefined} className="mt-4 text-meta text-muted-foreground">
        {visited ? `The host was asked to visit ${visited}.` : "Press a row in Needs attention to visit its record."}
      </p>
    </BentoVisit.Provider>
  );
}
